import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp.js';
import { 
  ArrowLeft, 
  Bot, 
  Send, 
  Paperclip, 
  AlertTriangle,
  MessageCircle,
  FileText,
  Image,
  X,
  Download
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

// Read Gemini API key from Vite env (set in your project root .env)
const GOOGLE_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

const AiAssistant = () => {
  const navigate = useNavigate();
  const { language, aiChats, addAiChat } = useApp();
  
  // Ensure aiChats is always an array
  const safeAiChats = aiChats || [];
  
  // Debug logging
  console.log('🔍 AI Assistant - aiChats:', aiChats);
  console.log('🔍 AI Assistant - aiChats length:', safeAiChats.length);
  console.log('🔍 AI Assistant - API Key available:', !!GOOGLE_API_KEY);
  
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showFilePreview, setShowFilePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const translations = {
    english: {
      aiAssistant: 'AI Legal Assistant',
      askQuestions: 'Ask legal questions & get AI-powered insights',
      disclaimer: 'This is not legal advice. For professional legal guidance, consult with a qualified lawyer.',
      sampleQuestions: 'Sample Questions:',
      askLegalQuestion: 'Ask your legal question...',
      send: 'Send',
      sampleQuestionsList: [
        'What are the steps to file a civil lawsuit?',
        'How do I register a trademark?',
        'What are tenant rights in rental agreements?',
        'How to draft a will?',
        'What are the legal requirements for starting a business?'
      ]
    },
    hindi: {
      aiAssistant: 'एआई कानूनी सहायक',
      askQuestions: 'कानूनी प्रश्न पूछें और एआई-संचालित अंतर्दृष्टि प्राप्त करें',
      disclaimer: 'यह कानूनी सलाह नहीं है। पेशेवर कानूनी मार्गदर्शन के लिए, योग्य वकील से परामर्श करें।',
      sampleQuestions: 'नमूना प्रश्न:',
      askLegalQuestion: 'अपना कानूनी प्रश्न पूछें...',
      send: 'भेजें',
      sampleQuestionsList: [
        'नागरिक मुकदमा दायर करने के क्या कदम हैं?',
        'ट्रेडमार्क कैसे पंजीकृत करें?',
        'किराया समझौतों में किरायेदार के क्या अधिकार हैं?',
        'वसीयत कैसे तैयार करें?',
        'व्यवसाय शुरू करने के कानूनी आवश्यकताएं क्या हैं?'
      ]
    }
  };

  const t = translations[language] || translations.english;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [safeAiChats]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      message: message.trim(),
      timestamp: new Date().toISOString(),
      sender: 'user'
    };

    addAiChat(userMessage);
    setMessage('');
    setIsTyping(true);

    try {
      if (!GOOGLE_API_KEY) {
        throw new Error('API key not configured');
      }

      console.log('🔍 Sending request to Gemini API...');
      console.log('🔍 API Key available:', !!GOOGLE_API_KEY);
      console.log('🔍 Message:', message.trim());

      // Use Google API for AI response
      const requestBody = {
        contents: [{
          parts: [{
            text: `You are a legal assistant. Provide helpful information about the following legal question, but make it clear you're not giving legal advice: ${message.trim()}`
          }]
        }]
      };

      console.log('🔍 Request body:', requestBody);

      // Try gemini-1.5-flash first (more reliable), fallback to gemini-pro if it fails
      let response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=' + GOOGLE_API_KEY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Error response from gemini-1.5-flash:', errorText);
        
        // Try fallback to gemini-pro
        console.log('🔍 Trying fallback to gemini-pro...');
        response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=' + GOOGLE_API_KEY, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          const fallbackErrorText = await response.text();
          console.error('🔍 Error response from fallback:', fallbackErrorText);
          throw new Error(`Failed to get AI response: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      console.log('🔍 Response data:', data);
      
      if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
        throw new Error('Invalid response format from API');
      }
      
      const aiResponseText = data.candidates[0].content.parts[0].text;

      const aiResponse = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        message: aiResponseText,
        timestamp: new Date().toISOString(),
        sender: 'ai'
      };
      
      addAiChat(aiResponse);
         } catch (error) {
       console.error('Error getting AI response:', error);
       
       // Intelligent fallback response based on the question
       const question = message.trim().toLowerCase();
       let fallbackResponse = '';
       
       if (question.includes('trademark') || question.includes('register')) {
         fallbackResponse = `**Trademark Registration Process**

Trademark registration is a legal process that protects your brand identity. Here's an overview of the typical process:

**Key Steps:**
• Conduct a comprehensive trademark search to ensure your mark is available
• File an application with the appropriate government office (like USPTO in the US)
• Provide detailed information about your mark, goods/services, and evidence of use
• Wait for examination and potential office actions
• Complete the registration process (typically 6-12 months)

**Important Considerations:**
• Choose a distinctive mark that's not generic or descriptive
• Ensure your mark doesn't conflict with existing registrations
• Be prepared for potential opposition from third parties
• Maintain your registration with periodic renewals

**Disclaimer:** This is not legal advice. For specific guidance tailored to your situation, I recommend consulting with a qualified trademark attorney who can review your specific circumstances and provide personalized legal advice.`;
       } else if (question.includes('lawsuit') || question.includes('civil')) {
         fallbackResponse = `**Civil Lawsuit Process**

Civil lawsuits are legal proceedings to resolve disputes between parties. Here's a general overview of the process:

**Main Stages:**
• **Filing:** Submit a complaint outlining your claims and damages
• **Service:** Notify the defendant of the lawsuit
• **Discovery:** Exchange information and evidence between parties
• **Pre-trial:** Motions, settlement discussions, and trial preparation
• **Trial:** Present evidence and arguments to a judge or jury
• **Judgment:** Court issues a decision and potential remedies

**Key Considerations:**
• Understand the statute of limitations for your claim
• Gather and preserve relevant evidence
• Consider alternative dispute resolution options
• Be prepared for the time and cost involved

**Disclaimer:** This is not legal advice. For personalized legal guidance, consult with a qualified attorney who can review your specific circumstances and provide appropriate legal counsel.`;
       } else if (question.includes('bail') || question.includes('bond')) {
         fallbackResponse = `**Bail and Bond Information**

Bail is a financial guarantee that ensures a defendant returns to court. Here's what you should know:

**Types of Bail:**
• **Cash bail:** Direct payment to the court
• **Property bond:** Using real estate as collateral
• **Surety bond:** Using a bail bondsman
• **Release on recognizance:** No payment required

**Factors Affecting Bail Amount:**
• Severity of the alleged crime
• Defendant's flight risk
• Criminal history and ties to the community
• Public safety considerations

**Important Notes:**
• Bail is typically returned if the defendant appears in court
• Bail bondsmen charge a non-refundable fee (usually 10-15%)
• Failure to appear can result in forfeiture of bail and additional charges

**Disclaimer:** This is not legal advice. For specific bail-related questions, consult with a criminal defense attorney who can provide guidance based on your particular situation.`;
       } else if (question.includes('tenant') || question.includes('rental') || question.includes('lease')) {
         fallbackResponse = `**Tenant Rights and Rental Agreements**

Understanding your rights as a tenant is crucial for a positive rental experience. Here's an overview:

**Key Tenant Rights:**
• Right to a habitable living space with basic amenities
• Right to privacy and reasonable notice for landlord entry
• Right to proper notice before eviction
• Right to security deposit return (minus legitimate deductions)
• Right to request repairs for essential services

**Important Lease Considerations:**
• Read and understand all terms before signing
• Document the condition of the property upon move-in
• Keep copies of all communications with your landlord
• Understand your responsibilities for maintenance and utilities
• Know the proper procedures for giving notice to vacate

**Common Issues:**
• Security deposit disputes
• Maintenance and repair requests
• Rent increases and lease renewals
• Eviction procedures and tenant protections

**Disclaimer:** This is not legal advice. For specific rental-related questions, consult with a qualified attorney who can provide guidance based on your local laws and specific circumstances.`;
       } else if (question.includes('will') || question.includes('estate')) {
         fallbackResponse = `**Wills and Estate Planning**

Estate planning is essential for protecting your assets and ensuring your wishes are carried out. Here's what you should know:

**Essential Documents:**
• **Will:** Outlines how your assets should be distributed
• **Power of Attorney:** Authorizes someone to act on your behalf
• **Healthcare Directive:** Specifies your medical treatment preferences
• **Trust:** Can help avoid probate and provide asset protection

**Key Requirements for a Valid Will:**
• Must be in writing and signed by the testator
• Requires at least two witnesses
• Should clearly identify beneficiaries and assets
• Must appoint an executor to carry out your wishes

**Important Considerations:**
• Review and update your estate plan regularly
• Consider the tax implications of your decisions
• Choose trustworthy individuals for important roles
• Keep your documents in a safe, accessible location

**Disclaimer:** This is not legal advice. Estate planning laws vary by state, so consult with an estate planning attorney for personalized advice.`;
       } else if (question.includes('business') || question.includes('company') || question.includes('start')) {
         fallbackResponse = `**Starting a Business - Legal Requirements**

Starting a business involves several legal considerations. Here's an overview of the key requirements:

**Business Structure Options:**
• **Sole Proprietorship:** Simplest form, but no liability protection
• **Partnership:** Shared ownership and responsibilities
• **LLC:** Limited liability with flexible management
• **Corporation:** More complex but offers strong liability protection

**Legal Requirements:**
• Register your business name and structure
• Obtain necessary licenses and permits
• Register for tax identification numbers
• Comply with employment laws if hiring
• Obtain appropriate insurance coverage

**Important Documents:**
• Business plan and financial projections
• Operating agreements or bylaws
• Employment contracts and policies
• Vendor and client agreements
• Intellectual property protection

**Disclaimer:** This is not legal advice. The specific requirements depend on your business type and location. Consult with a business attorney for guidance tailored to your situation.`;
       } else {
         fallbackResponse = `**Legal Information**

I understand you're seeking legal information. While I can provide general guidance, it's important to note that legal matters often require personalized advice based on your specific circumstances.

**General Recommendations:**
• Consult with a qualified attorney for your specific situation
• Research relevant laws and regulations in your jurisdiction
• Keep detailed records and documentation
• Consider alternative dispute resolution options when appropriate
• Stay informed about your rights and responsibilities

**When to Seek Legal Counsel:**
• Complex legal matters or disputes
• Significant financial or personal consequences
• Unclear legal requirements or procedures
• Potential criminal implications
• Business or employment-related issues

**Disclaimer:** This is not legal advice. For personalized legal guidance, please consult with a qualified attorney who can review your specific circumstances and provide appropriate legal counsel.`;
       }
       
       const aiResponse = {
         id: (Date.now() + 1).toString(),
         type: 'ai',
         message: fallbackResponse,
         timestamp: new Date().toISOString(),
         sender: 'ai'
       };
       addAiChat(aiResponse);
       toast.error('There was an issue connecting to the AI service');
     } finally {
      setIsTyping(false);
    }
  };

  const handleSampleQuestion = (question) => {
    setMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (file) => {
    if (uploadedFiles.length >= 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    const fileType = file.type.split('/')[0];
    const isImage = fileType === 'image';
    const isDocument = file.type.includes('pdf') || file.type.includes('doc') || file.type.includes('txt');

    if (!isImage && !isDocument) {
      toast.error('Only images and documents are allowed');
      return;
    }

    const newFile = {
      id: Date.now(),
      name: file.name,
      type: isImage ? 'image' : 'document',
      fileType: file.type,
      fileSize: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
      fileUrl: URL.createObjectURL(file),
      timestamp: new Date().toISOString()
    };

    setUploadedFiles(prev => [...prev, newFile]);
    toast.success('File uploaded successfully');
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  return (
         <div className="min-h-screen bg-white flex flex-col pb-32">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </button>
          <div className="flex-1 flex flex-col items-center">
            <div className="flex items-center justify-center mb-2">
              <Bot className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="font-semibold text-base text-gray-900 text-center">{t.aiAssistant}</h1>
            <p className="text-sm text-gray-500 text-center">{t.askQuestions}</p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 px-4 py-3 border-b border-yellow-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            {t.disclaimer}
          </p>
        </div>
      </div>

             {/* Chat Messages */}
       <div className="flex-1 overflow-y-auto px-4 py-4 pb-8 bg-gray-50">
        <div className="space-y-3 max-w-2xl mx-auto">
        
        {/* Welcome Message */}
        {safeAiChats.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-8"
          >
            <div className="bg-white rounded-2xl px-6 py-8 border border-gray-200 shadow-md max-w-md text-center">
              <Bot className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Welcome to AI Legal Assistant</h3>
              <p className="text-sm text-gray-600 mb-4">
                Ask me any legal questions and I'll provide helpful information. Remember, this is for general guidance only.
              </p>
              <div className="text-xs text-gray-500">
                💡 Try asking about legal procedures, rights, or general legal concepts
              </div>
            </div>
          </motion.div>
        )}
        
        {safeAiChats.map((chat) => (
          <motion.div
            key={chat.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${chat.sender === 'user' ? 'justify-end' : 'justify-start'} mb-4`}
          >
            <div
              className={`max-w-xs lg:max-w-md rounded-2xl px-4 py-3 shadow-md ${
                chat.sender === 'user'
                  ? 'bg-purple-600 text-white rounded-br-md'
                  : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
              }`}
            >
              {chat.sender === 'ai' && (
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-gray-500">AI Assistant</span>
                </div>
              )}
              <p className={`text-sm leading-relaxed break-words ${chat.sender === 'user' ? 'text-white' : ''}`}>{chat.message}</p>
              <p className={`text-xs mt-2 ${chat.sender === 'user' ? 'text-white opacity-75' : 'opacity-70'}`}>
                {new Date(chat.timestamp).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </p>
            </div>
          </motion.div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start mb-4"
          >
            <div className="bg-white rounded-2xl px-4 py-3 border border-gray-200 shadow-md">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-4 h-4 text-purple-600" />
                <span className="text-xs text-gray-500">AI Assistant</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-600">AI is thinking...</span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Sample Questions */}
      {(safeAiChats.length === 0 || safeAiChats.length === 1) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 py-4 border-t border-gray-200 bg-white"
        >
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            {t.sampleQuestions}
          </h3>
          <div className="space-y-2">
            {t.sampleQuestionsList.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSampleQuestion(question)}
                className="w-full text-left p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors duration-200"
              >
                <p className="text-sm text-purple-600">
                  {question}
                </p>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Uploaded Files Display */}
      {uploadedFiles.length > 0 && (
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="max-w-2xl mx-auto">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Uploaded Files ({uploadedFiles.length}/5)</h4>
            <div className="grid grid-cols-2 gap-3">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {file.type === 'image' ? (
                    <Image className="w-6 h-6 text-purple-600" />
                  ) : (
                    <FileText className="w-6 h-6 text-purple-600" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{file.fileSize}</p>
                  </div>
                  <button
                    onClick={() => removeFile(file.id)}
                    className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Attachment Menu */}
      {showAttachmentMenu && (
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
            <button
              onClick={() => {
                fileInputRef.current?.click();
                setShowAttachmentMenu(false);
              }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <FileText className="w-6 h-6 text-purple-600" />
              <span className="text-xs font-medium text-gray-900">Document</span>
            </button>
            <button
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) handleFileUpload(file);
                };
                input.click();
                setShowAttachmentMenu(false);
              }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <Image className="w-6 h-6 text-purple-600" />
              <span className="text-xs font-medium text-gray-900">Photo</span>
            </button>
          </div>
        </div>
      )}

             {/* Input Area - Positioned above bottom navigation */}
       <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50 shadow-lg">
        <div className="flex items-end gap-3 max-w-2xl mx-auto">
          <button
            onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
            className={`p-2 rounded-full transition-colors ${
              showAttachmentMenu ? 'bg-purple-100 text-purple-600' : 'hover:bg-gray-100 text-purple-600 dark:text-purple-400'
            }`}
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <div className="flex-1 relative">
                         <textarea
               value={message}
               onChange={(e) => setMessage(e.target.value)}
               onKeyPress={handleKeyPress}
               placeholder={t.askLegalQuestion}
               className="w-full px-3 py-2 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 text-gray-900 resize-none"
               rows="1"
               style={{ minHeight: '44px', maxHeight: '100px' }}
             />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!message.trim() || isTyping}
            className="p-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:text-gray-500 text-white rounded-full transition-colors disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) handleFileUpload(file);
        }}
        accept=".pdf,.doc,.docx,.txt"
      />

      {/* File Preview Modal */}
      {showFilePreview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-4 w-full max-w-lg max-h-[90vh] overflow-auto shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {showFilePreview.name}
              </h3>
              <button
                onClick={() => setShowFilePreview(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Image Preview */}
              {showFilePreview.type === 'image' && (
                <div className="flex justify-center">
                  <img
                    src={showFilePreview.fileUrl}
                    alt={showFilePreview.name}
                    className="max-w-full max-h-96 object-contain rounded-lg"
                  />
                </div>
              )}
              
              {/* PDF Preview */}
              {showFilePreview.type === 'document' && showFilePreview.fileType === 'pdf' && (
                <div className="flex justify-center">
                  <iframe
                    src={showFilePreview.fileUrl}
                    className="w-full h-96 rounded-lg border border-gray-200"
                    title={showFilePreview.name}
                  />
                </div>
              )}
              
              {/* Other Document Types */}
              {showFilePreview.type === 'document' && showFilePreview.fileType !== 'pdf' && (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-2">
                    Preview not available for this file type
                  </p>
                  <p className="text-sm text-gray-500">
                    {showFilePreview.name} • {showFilePreview.fileSize}
                  </p>
                </div>
              )}
              
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = showFilePreview.fileUrl;
                    link.download = showFilePreview.name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => setShowFilePreview(null)}
                  className="py-2 px-4 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiAssistant;